import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { useLocalListSearch } from '@/hooks/useLocalListSearch';
import { feedbackTypesQueryOptions } from '../api/feedbackTypes.api';
import { useFeedbackTypeActionModals } from '../hooks/useFeedbackTypeActionModals';
import type { FeedbackType } from '../types/feedbackType.types';
import { getFeedbackTypeColumns } from './FeedbackTypeColumns';
import { FeedbackTypeDeleteConfirmation } from './FeedbackTypeDeleteConfirmation';
import { FeedbackTypeFormModal } from './FeedbackTypeFormModal';

const route = getRouteApi('/_auth/settings/feedback-types');

const toSearchableValues = (feedbackType: FeedbackType) => [
  feedbackType.name,
  feedbackType.code,
  feedbackType.description,
];

export function FeedbackTypeListTable() {
  const { q: appliedSearchText } = route.useSearch();
  const navigate = route.useNavigate();

  const {
    modalState,
    feedbackTypeToDelete,
    openCreateModal,
    openEditModal,
    closeModal,
    openDeleteModal,
    closeDeleteModal,
  } = useFeedbackTypeActionModals();

  const { data, isLoading, error, refetch } = useQuery(feedbackTypesQueryOptions());

  const { searchText, setSearchText, visibleRows } = useLocalListSearch({
    rows: data,
    appliedText: appliedSearchText,
    onApply: (text) => void navigate({ search: { q: text }, replace: true }),
    toSearchableValues,
  });

  const columns = useMemo(
    () =>
      getFeedbackTypeColumns(appliedSearchText, {
        onEdit: openEditModal,
        onDelete: openDeleteModal,
      }),
    [appliedSearchText, openEditModal, openDeleteModal],
  );

  return (
    <>
      <div className={FIT_TO_SCREEN_PAGE_CLASS}>
        <PageHeader
          title="Feedback Types"
          search={{
            value: searchText,
            onChange: setSearchText,
            placeholder: 'Search by code or name',
          }}
          createAction={{ label: 'Add Feedback Type', onClick: openCreateModal }}
        />

        {error ? (
          <ErrorState
            error={error}
            fallbackTitle="Could not load feedback types"
            onRetry={() => void refetch()}
            fill
          />
        ) : (
          <DataTable
            data={visibleRows}
            columns={columns}
            isLoading={isLoading}
            stickyColumnId="name"
            className="lg:flex-1"
          />
        )}
      </div>

      <FeedbackTypeFormModal
        feedbackType={modalState?.feedbackType ?? null}
        open={modalState !== null}
        onOpenChange={(open) => !open && closeModal()}
      />

      <FeedbackTypeDeleteConfirmation
        feedbackType={feedbackTypeToDelete}
        open={feedbackTypeToDelete !== null}
        onOpenChange={(open) => !open && closeDeleteModal()}
      />
    </>
  );
}
